import { t } from "@lingui/macro";
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  CircleNotch,
  ClockClockwise,
  CubeFocus,
  FilePdf,
  Hash,
  LineSegment,
  LinkSimple,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  FloppyDisk,
} from "@phosphor-icons/react";
import { Button, Separator, Toggle, Tooltip } from "@reactive-resume/ui";
import { motion } from "framer-motion";

import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore, useTemporalResumeStore } from "@/client/stores/resume";

export const BuilderToolbar = () => {
  const { toast } = useToast();
  const setValue = useResumeStore((state) => {
    return state.setValue;
  });

  const undo = useTemporalResumeStore((state) => state.undo);
  const redo = useTemporalResumeStore((state) => state.redo);
  const frameRef = useBuilderStore((state) => state.frame.ref);

  const id = useResumeStore((state) => state.resume.id);
  const isPublic = useResumeStore((state) => state.resume.visibility === "public");
  const breakLine = useResumeStore(
    (state) => state.resume.data.metadata.page.options.breakLine === false,
  );
  const pageOptions = useResumeStore((state) => state.resume.data.metadata.page.options);

  const basics = useResumeStore((state) => state.resume.data.basics);

  const setRewardPoints = useResumeStore((state) => state.setRewardPoints);

  const { printResume, loading } = usePrintResume();
  const cdnBaseURL = import.meta.env.VITE_ApiBaseURL;

  const cookies = document.cookie.split("; ").reduce((acc: any, cookie) => {
    const [key, value] = cookie.split("=");
    acc[key] = decodeURIComponent(value);
    return acc;
  }, {});

  const userToken = cookies["token"];
  if (!userToken) {
    console.warn("No token found in cookies, skipping reward API call");
    return null;
  }

  const onPrint = async () => {
    try {
      // 1. Call your resume print API
      const url = await printResume({ id });
      const payload = {
        event_key: "cv_builder",
        action: "added",
        reference_id: null,
      };

      // Trigger reward points API
      const rewardRes = await fetch(`${cdnBaseURL}/rewards/v1/trigger-youth-reward-points`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(payload),
      });

      const rewardResponse = await rewardRes.json();

      if (rewardResponse?.status !== "failed") {
        const rewardRes = await fetch(
          `${import.meta.env.VITE_ApiBaseURL}/rewards/v1/reward-points-distribution-list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          },
        );

        const rewardData = await rewardRes.json();

        const cvBuilderPoints =
          rewardData?.data?.find((item: any) => item.event_name === "CV Builder")?.points || 0;
        console.log("cvBuilderPoints", cvBuilderPoints);

        setRewardPoints(cvBuilderPoints);
        setValue("basics.rewardPoints", cvBuilderPoints);
        return {
          rewardTriggered: rewardResponse,
          rewardData: rewardData?.data || [],
        };
      }

      // 3. Open resume
      const win = window.open(url, "_blank");
      if (win) win.focus();
    } catch (err) {
      console.error("Error in onPrint:", err);
      return null;
    }
  };

  const onCopy = async () => {
    const url: any = await printResume({ id });
    await navigator.clipboard.writeText(url);

    toast({
      variant: "success",
      title: t`A link has been copied to your clipboard.`,
      description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
    });
  };
  const onSave = () => {
    try {
      // Perform the save action here
      setValue("metadata.page.options.breakLine", false);
      // toast.success("Resume saved successfully!");
    } catch (error) {
      // toast.error("Failed to save resume. Please try again later.");
      console.error("Error saving resume:", error);
    }
  };

  const onZoomIn = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_IN" }, "*");
  const onZoomOut = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_OUT" }, "*");
  const onResetView = () => frameRef?.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");
  const onCenterView = () => frameRef?.contentWindow?.postMessage({ type: "CENTER_VIEW" }, "*");

  return (
    <motion.div className="fixed inset-x-0 bottom-0 mx-auto  py-6 text-center md:block ">
      <div className="inline-flex items-center justify-center rounded-full bg-background px-4 shadow-xl custom-toolbar-xs">
        {/* <Tooltip content={t`Undo`}>
          <Button size="icon" variant="ghost" className="rounded-none" onClick={() => undo()}>
            <ArrowCounterClockwise />
          </Button>
        </Tooltip>

        <Tooltip content={t`Redo`}>
          <Button size="icon" variant="ghost" className="rounded-none" onClick={() => redo()}>
            <ArrowClockwise />
          </Button>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" /> */}

        <Tooltip content={t`Zoom In`}>
          <Button size="icon" variant="ghost" className="rounded-none" onClick={onZoomIn}>
            <MagnifyingGlassPlus />
          </Button>
        </Tooltip>

        <Tooltip content={t`Zoom Out`}>
          <Button size="icon" variant="ghost" className="rounded-none" onClick={onZoomOut}>
            <MagnifyingGlassMinus />
          </Button>
        </Tooltip>

        <Tooltip content={t`Reset Zoom`}>
          <Button size="icon" variant="ghost" className="rounded-none" onClick={onResetView}>
            <ClockClockwise />
          </Button>
        </Tooltip>

        <Tooltip content={t`Center Artboard`}>
          <Button size="icon" variant="ghost" className="rounded-none" onClick={onCenterView}>
            <CubeFocus />
          </Button>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" />
        {/* 
        <Tooltip content={t`Toggle Page Break Line`}>
          <Toggle
            className="rounded-none"
            pressed={pageOptions.breakLine}
            onPressedChange={(pressed) => {
              setValue("metadata.page.options.breakLine", pressed);
            }}
          >
            <LineSegment />
          </Toggle>
        </Tooltip> */}

        <Tooltip content={t`Toggle Page Numbers`}>
          <Toggle
            className="rounded-none hidden page_number"
            pressed={pageOptions.pageNumbers}
            onPressedChange={(pressed) => {
              setValue("metadata.page.options.pageNumbers", pressed);
            }}
          >
            <Hash />
          </Toggle>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" />

        {/* <Tooltip content={t`Copy Link to Resume`}>
          <Button
            size="icon"
            variant="ghost"
            className="rounded-none"
            onClick={onCopy}
            disabled={!isPublic}
          >
            <LinkSimple />
          </Button>
        </Tooltip> */}

        <Tooltip content={t`Save`}>
          <Button
            size="icon"
            variant="ghost"
            onClick={(pressed) => {
              onSave;
            }}
            disabled={loading}
            className="rounded-none"
          >
            {loading ? <CircleNotch className="animate-spin" /> : <FloppyDisk />}
          </Button>
        </Tooltip>

        <Tooltip content={t`Download PDF`}>
          <Button
            size="icon"
            variant="ghost"
            onClick={onPrint}
            disabled={loading}
            className="rounded-none"
          >
            {loading ? <CircleNotch className="animate-spin" /> : <FilePdf />}
          </Button>
        </Tooltip>
      </div>
    </motion.div>
  );
};
